import { useState } from 'react';
import { stagger } from '@/lib/motion';
import { EmptyState } from '@/components/EmptyState';
import { Tag } from '@/components/badges';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { UsageReport } from '@/api/schemasUsage';
import { useSeesProviders } from '@/lib/useAudience';
import { BREAKDOWN_LABEL, breakdownRows, breakdownTabs, formatBytes, formatSeconds, isEmptyReport, monthLabel, number, type BreakdownRow, type BreakdownTab } from './usageModel';

function Totals({ report }: { report: UsageReport }) {
  const t = report.totals;
  const items = [
    { label: 'Builds', value: number(t.builds) },
    { label: 'Build minutes', value: number(t.billableMinutes) },
    { label: 'Build service time', value: formatSeconds(t.providerSeconds) },
    { label: 'Queue time', value: formatSeconds(t.queuedSeconds) },
    { label: 'Artifacts', value: formatBytes(t.artifactBytes) },
  ];
  return (
    <dl data-testid="usage-totals" className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
      {items.map((i) => (
        <div key={i.label} className="flex min-w-0 flex-col gap-0.5">
          <dt className="text-[13px] text-muted-foreground">{i.label}</dt>
          <dd className="tabular font-mono text-lg font-semibold text-foreground">{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Name({ row }: { row: BreakdownRow }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate font-medium text-foreground">{row.label}</span>
      {row.sub ? <span className="truncate font-mono text-xs text-subtle">{row.sub}</span> : null}
    </div>
  );
}

function ShareBar({ row }: { row: BreakdownRow }) {
  return (
    <div className="flex items-center gap-2">
      <Progress value={row.share} aria-label={`${row.label}: ${row.share}% of the minutes`} className="h-1.5 w-20" indicatorClassName="bg-primary" />
      <span className="tabular w-9 text-right font-mono text-xs text-subtle">{row.share}%</span>
    </div>
  );
}

function RowsTable({ rows, tab }: { rows: BreakdownRow[]; tab: BreakdownTab }) {
  return (
    <>
      <div className="hidden sm:block">
        <Table data-testid={`usage-table-${tab}`}>
          <TableHeader>
            <TableRow>
              <TableHead>{BREAKDOWN_LABEL[tab].replace(/s$/, '')}</TableHead>
              <TableHead className="text-right">Builds</TableHead>
              <TableHead className="text-right">Minutes</TableHead>
              <TableHead>Share</TableHead>
              <TableHead className="text-right">Build service time</TableHead>
              <TableHead className="text-right">Queue</TableHead>
              <TableHead className="text-right">Artifacts</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} data-testid={`usage-row-${r.key}`}>
                <TableCell className="max-w-0 w-[28%]"><Name row={r} /></TableCell>
                <TableCell className="tabular text-right font-mono">{number(r.builds)}</TableCell>
                <TableCell className="tabular text-right font-mono">{number(r.minutes)}</TableCell>
                <TableCell><ShareBar row={r} /></TableCell>
                <TableCell className="tabular text-right font-mono text-muted-foreground">{r.providerTime}</TableCell>
                <TableCell className="tabular text-right font-mono text-muted-foreground">{r.queued}</TableCell>
                <TableCell className="tabular text-right font-mono text-muted-foreground">{r.storage}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ul aria-label={`Usage by ${BREAKDOWN_LABEL[tab].toLowerCase()}`} className="flex flex-col gap-2 p-3 sm:hidden">
        {rows.map((r, i) => (
          <li key={r.key} style={stagger(i)} className="enter flex flex-col gap-2 rounded-lg border bg-card p-3 dark:bg-background">
            <div className="flex items-start justify-between gap-3">
              <Name row={r} />
              <Tag>{number(r.minutes)} min</Tag>
            </div>
            <ShareBar row={r} />
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>{number(r.builds)} builds</span>
              <span>{r.providerTime} on the build service</span>
              <span>{r.storage}</span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Same geometry as the loaded card: totals row, tab strip, five table rows. */
export function BreakdownSkeleton() {
  return (
    <Card data-testid="usage-breakdown-loading" aria-busy="true" className="enter" style={stagger(6)}>
      <CardHeader>
        <Skeleton className="h-5 w-40" />
        <Skeleton className="mt-1 h-4 w-64 max-w-full" />
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <Skeleton className="h-3.5 w-20" />
              <Skeleton className="h-6 w-16" />
            </div>
          ))}
        </div>
        <Skeleton className="h-9 w-full max-w-sm" />
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-11 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * What the month's builds used, by project, app, platform and (platform operators only, SPEC decision 23) provider
 * account; a designed empty state for a month without builds.
 */
export function UsageBreakdown({ report }: { report: UsageReport }) {
  const tabs = breakdownTabs(useSeesProviders());
  const [tab, setTab] = useState<BreakdownTab>('projects');
  const rows = breakdownRows(report, tab);
  return (
    <Card data-testid="usage-breakdown" className="enter" style={stagger(6)}>
      <CardHeader>
        <CardTitle>{monthLabel(report.month)}</CardTitle>
        <CardDescription>Builds that ended in this calendar month (UTC). Build service minutes are rounded up per build.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5 px-0 sm:px-6">
        {isEmptyReport(report) ? (
          <EmptyState
            testId="usage-empty"
            icon="query_stats"
            title={`No builds ended in ${monthLabel(report.month)}`}
            description="Usage is recorded when a build finishes: minutes on the build service, queue time and the artifacts it produced. Run a build and it shows up here."
          />
        ) : (
          <>
            <div className="px-4 sm:px-0">
              <Totals report={report} />
            </div>
            <Tabs value={tab} onValueChange={(v) => setTab(v as BreakdownTab)} className="gap-3">
              <TabsList className="mx-4 sm:mx-0" aria-label="Group usage by">
                {tabs.map((t) => (
                  <TabsTrigger key={t} value={t} data-testid={`usage-tab-${t}`} className="min-h-11 sm:min-h-8">
                    {BREAKDOWN_LABEL[t]}
                  </TabsTrigger>
                ))}
              </TabsList>
              <RowsTable rows={rows} tab={tab} />
            </Tabs>
          </>
        )}
      </CardContent>
    </Card>
  );
}
